import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { statusToneClasses } from '../../lib/statusTone';

const successTone = statusToneClasses('success');
const warningTone = statusToneClasses('warning');
const dangerTone = statusToneClasses('danger');
const infoTone = statusToneClasses('info');

const STATUS_BADGE_VARIANTS = {
  draft: cn(warningTone.subtleBackground, warningTone.subtleText),
  finalized: cn(successTone.subtleBackground, successTone.subtleText),
  scaduto: cn(dangerTone.subtleBackground, dangerTone.subtleText),
  conforme: cn(successTone.subtleBackground, successTone.subtleText),
  'non-conforme': cn(dangerTone.subtleBackground, dangerTone.subtleText),
  success: cn(successTone.subtleBackground, successTone.subtleText),
  warning: cn(warningTone.subtleBackground, warningTone.subtleText),
  error: cn(dangerTone.subtleBackground, dangerTone.subtleText),
  info: cn(infoTone.subtleBackground, infoTone.subtleText),
} as const;

const badgeVariants = cva(
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide transition-colors border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-neutral-100 text-text-secondary',
        secondary: 'border-transparent bg-brand-blue-100 text-brand-blue-700',
        outline: 'border border-[var(--border-default)] text-text-secondary',

        // Status-specific variants from design mockups
        draft: STATUS_BADGE_VARIANTS.draft,
        finalized: STATUS_BADGE_VARIANTS.finalized,
        scaduto: STATUS_BADGE_VARIANTS.scaduto,
        conforme: STATUS_BADGE_VARIANTS.conforme,
        'non-conforme': STATUS_BADGE_VARIANTS['non-conforme'],
        'non-verificabile': 'border-transparent bg-neutral-200 text-text-secondary',
        'non-applicabile': 'border-transparent bg-neutral-100 text-text-tertiary',

        // Additional status variants
        success: STATUS_BADGE_VARIANTS.success,
        warning: STATUS_BADGE_VARIANTS.warning,
        error: STATUS_BADGE_VARIANTS.error,
        info: STATUS_BADGE_VARIANTS.info,
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
